import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminOutreachCreditTransactionInput } from '@workspace/api-backend'
import {
  v2AdminCreateOutreachCreditTransactionMutation,
  v2AdminGetOutreachBillingDetailOptions,
  v2AdminListOutreachBillingQueryKey,
} from '@workspace/api-backend/react-query'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { TextInput } from '@workspace/ui/components/text-input'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useId, useState } from 'react'

export interface BillingRow {
  startupId: string
  startupName: string
  status: string | null
  balanceCents: number
  pendingAccrualsCents: number
  lifetimeToppedUpCents: number
  lifetimeSpentCents: number
  viewCount: number
  callCount: number
  lastTransactionAt: string | null
  lastAccrualAt: string | null
}

export function OutreachBillingRow({ row }: { row: BillingRow }) {
  const [open, setOpen] = useState(false)
  const [refundOpen, setRefundOpen] = useState(false)

  const lastActivity =
    [row.lastTransactionAt, row.lastAccrualAt]
      .filter((d): d is string => !!d)
      .map((d) => new Date(d).getTime())
      .reduce<number | null>((max, t) => (max === null ? t : Math.max(max, t)), null) ?? null

  return (
    <>
      <tr
        className="border-t border-border/40 transition-colors hover:bg-muted/10"
        onClick={() => setOpen(!open)}
      >
        <td className="px-2 py-2 align-middle">
          <button type="button" className="text-muted-foreground">
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        </td>
        <td className="px-4 py-2 align-middle font-medium text-foreground">{row.startupName}</td>
        <td className="px-4 py-2 align-middle">
          {row.status ? (
            <StatusChip status={row.status} />
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </td>
        <td
          className={cn(
            'px-4 py-2 text-right font-mono font-semibold align-middle',
            row.balanceCents < 0 ? 'text-amber-400' : 'text-foreground'
          )}
        >
          {formatUsd(row.balanceCents)}
        </td>
        <td className="px-4 py-2 text-right font-mono text-muted-foreground align-middle">
          {formatUsd(row.lifetimeToppedUpCents)}
        </td>
        <td className="px-4 py-2 text-right font-mono text-muted-foreground align-middle">
          {formatUsd(-row.lifetimeSpentCents)}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground align-middle">
          {row.viewCount}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground align-middle">
          {row.callCount}
        </td>
        <td className="px-4 py-2 text-right text-[11px] text-muted-foreground/80 align-middle">
          {lastActivity ? formatRelative(new Date(lastActivity)) : '—'}
        </td>
        <td className="px-4 py-2 text-right align-middle">
          <SecondaryButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              setRefundOpen(true)
            }}
          >
            Refund / adjust
          </SecondaryButton>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-border/40 bg-muted/10">
          <td></td>
          <td colSpan={9} className="px-4 py-4">
            <BillingDetail startupId={row.startupId} />
          </td>
        </tr>
      )}

      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        startupId={row.startupId}
        startupName={row.startupName}
      />
    </>
  )
}

export function OutreachBillingTableHeader() {
  return (
    <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
      <tr>
        <th className="w-8 px-2 py-2"></th>
        <th className="px-4 py-2 text-left font-medium">Startup</th>
        <th className="px-4 py-2 text-left font-medium">Status</th>
        <th className="px-4 py-2 text-right font-medium">Balance</th>
        <th className="px-4 py-2 text-right font-medium">Topped up</th>
        <th className="px-4 py-2 text-right font-medium">Spent</th>
        <th className="px-4 py-2 text-right font-medium">Views</th>
        <th className="px-4 py-2 text-right font-medium">Calls</th>
        <th className="px-4 py-2 text-right font-medium">Last activity</th>
        <th className="px-4 py-2 text-right font-medium"></th>
      </tr>
    </thead>
  )
}

function BillingDetail({ startupId }: { startupId: string }) {
  const { data, isLoading } = useQuery(
    v2AdminGetOutreachBillingDetailOptions({ path: { id: startupId } })
  )
  if (isLoading) {
    return <div className="text-xs text-muted-foreground">Loading detail…</div>
  }
  if (data?.status !== 'ok') {
    return <div className="text-xs text-amber-400">Failed to load detail.</div>
  }
  const detail = data.data

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Recent transactions
        </div>
        {detail.recentTransactions.length === 0 ? (
          <div className="mt-2 rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-xs text-muted-foreground">
            No transactions yet.
          </div>
        ) : (
          <ol className="mt-2 space-y-1.5">
            {detail.recentTransactions.map((tx) => (
              <li
                key={tx.id}
                className="flex items-baseline justify-between gap-2 rounded-md bg-background/40 px-3 py-1.5 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-foreground">{txnLabel(tx.type)}</span>
                    <span className="text-[10px] text-muted-foreground/70">
                      {formatRelative(new Date(tx.createdAt))}
                    </span>
                  </div>
                  {tx.note && (
                    <div className="truncate text-[10px] text-muted-foreground">{tx.note}</div>
                  )}
                </div>
                <span
                  className={cn(
                    'font-mono font-semibold tabular-nums',
                    tx.amountCents >= 0 ? 'text-emerald-400' : 'text-muted-foreground'
                  )}
                >
                  {formatUsdSigned(tx.amountCents)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Recent accruals
        </div>
        {detail.recentAccruals.length === 0 ? (
          <div className="mt-2 rounded-md border border-dashed border-border/60 px-3 py-4 text-center text-xs text-muted-foreground">
            No accruals yet.
          </div>
        ) : (
          <ol className="mt-2 space-y-1.5">
            {detail.recentAccruals.map((a) => (
              <li
                key={a.id}
                className="flex items-baseline justify-between gap-2 rounded-md bg-background/40 px-3 py-1.5 text-xs"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-foreground">
                      {a.type === 'VIEW' ? 'Profile view' : 'Call booked'}
                    </span>
                    {a.settledAt ? (
                      <Badge variant="outline" size="sm">
                        settled
                      </Badge>
                    ) : (
                      <Badge variant="secondary" size="sm">
                        pending
                      </Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground/70">
                      {formatRelative(new Date(a.occurredAt))}
                    </span>
                  </div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {a.investorName ?? '—'}
                    {a.contactName ? ` · ${a.contactName}` : ''}
                    {a.contactTitle ? `, ${a.contactTitle}` : ''}
                  </div>
                </div>
                <span className="font-mono font-semibold tabular-nums text-muted-foreground">
                  {formatUsdSigned(-a.amountCents)}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

function RefundDialog({
  open,
  onOpenChange,
  startupId,
  startupName,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  startupId: string
  startupName: string
}) {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<'REFUND' | 'ADJUSTMENT'>('REFUND')
  const [amountStr, setAmountStr] = useState('')
  const [note, setNote] = useState('')
  const amountId = useId()
  const noteId = useId()

  const create = useMutation({
    ...v2AdminCreateOutreachCreditTransactionMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: v2AdminListOutreachBillingQueryKey() })
      queryClient.invalidateQueries({
        queryKey: v2AdminListOutreachBillingQueryKey().slice(0, -1),
      })
      onOpenChange(false)
      setAmountStr('')
      setNote('')
    },
  })

  const rawAmount = Math.round(Number(amountStr) * 100)
  const amountCents = kind === 'REFUND' ? -Math.abs(rawAmount) : rawAmount
  const valid = !!amountStr && rawAmount !== 0 && note.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund / Adjust — {startupName}</DialogTitle>
          <DialogDescription>
            Records an internal CreditTransaction with your admin id. Does not call Stripe — issue
            the actual card refund manually in the Stripe Dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setKind('REFUND')}
              className={cn(
                'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                kind === 'REFUND'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border/60 bg-background/40 text-muted-foreground hover:text-foreground'
              )}
            >
              Refund (reduces balance)
            </button>
            <button
              type="button"
              onClick={() => setKind('ADJUSTMENT')}
              className={cn(
                'rounded-lg border px-3 py-2 text-xs font-medium transition-colors',
                kind === 'ADJUSTMENT'
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border/60 bg-background/40 text-muted-foreground hover:text-foreground'
              )}
            >
              Adjustment (signed)
            </button>
          </div>

          <div>
            <label
              htmlFor={amountId}
              className="text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Amount in USD {kind === 'ADJUSTMENT' ? '(use negative to reduce balance)' : ''}
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">$</span>
              <TextInput
                id={amountId}
                type="number"
                inputSize="sm"
                step={1}
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                className="max-w-[160px]"
                placeholder={kind === 'REFUND' ? '50' : '-25'}
              />
            </div>
          </div>

          <div>
            <label
              htmlFor={noteId}
              className="text-[11px] uppercase tracking-wider text-muted-foreground"
            >
              Reason (required, audited)
            </label>
            <TextInput
              id={noteId}
              inputSize="sm"
              className="mt-1.5"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Founder ended campaign — refunding leftover credit"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              create.mutate({
                body: {
                  startupId,
                  amountCents,
                  kind,
                  note: note.trim(),
                },
              })
            }
            disabled={!valid || create.isPending}
          >
            {create.isPending
              ? 'Saving…'
              : kind === 'REFUND'
                ? 'Record refund'
                : 'Apply adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function StatusChip({ status }: { status: string }) {
  if (status === 'READY') {
    return (
      <Badge variant="success" size="sm">
        Active
      </Badge>
    )
  }
  if (status === 'PAUSED_DUE_TO_BUDGET') {
    return (
      <Badge variant="warning" size="sm">
        Budget paused
      </Badge>
    )
  }
  if (status === 'PAUSED') {
    return (
      <Badge variant="warning" size="sm">
        Paused
      </Badge>
    )
  }
  if (status === 'ERROR') {
    return (
      <Badge variant="destructive" size="sm">
        Error
      </Badge>
    )
  }
  return (
    <Badge variant="secondary" size="sm">
      {status.toLowerCase().replace(/_/g, ' ')}
    </Badge>
  )
}

function txnLabel(type: AdminOutreachCreditTransactionInput['type']): string {
  switch (type) {
    case 'OUTREACH_TOPUP':
      return 'Deposit'
    case 'OUTREACH_SETTLEMENT':
      return 'Weekly settlement'
    case 'WALLET_TOPUP':
      return 'Wallet top-up'
    case 'PITCH_SESSION_CHARGE':
      return 'Pitch session'
    case 'EVALUATION_CHARGE':
      return 'Fund evaluation'
    case 'WALLET_CREDIT':
      return 'Wallet credit'
    default:
      return 'Refund / Adjustment'
  }
}

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

function formatUsdSigned(cents: number): string {
  const sign = cents < 0 ? '-' : '+'
  return `${sign}${formatUsd(Math.abs(cents))}`
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime()
  const mins = Math.round(diffMs / (60 * 1000))
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
